import type { Equipment } from '../types/equipment';

/**
 * Lower-cased, with spaces, dashes, dots and slashes removed, so a rego typed
 * "ABC 123" finds one stored as "ABC-123" and "1ABC123" finds "1 ABC 123".
 */
function squash(s: string): string {
  return s.toLowerCase().replace(/[\s\-./]+/g, '');
}

/** Everything a person might type to find a machine, as one searchable string. */
export function equipmentSearchText(eq: Equipment, typeLabel?: string | null): string {
  return [
    eq.name,
    eq.make,
    eq.model,
    eq.year_of_manufacture != null ? String(eq.year_of_manufacture) : null,
    eq.colour,
    eq.rego_number,
    eq.rego_state,
    eq.vin,
    eq.serial_number,
    eq.engine_number,
    typeLabel,
    eq.custom_type_label,
    eq.farm_name,
    eq.location_name,
    eq.assigned_user_name,
    eq.active_session_operator,
    ...(eq.current_implements ?? []).map(i => i.name),
  ].filter((v): v is string => !!v).join(' ').toLowerCase();
}

/**
 * Every word of the query must appear somewhere — "deere 6140" finds the John
 * Deere 6140M, "ute sarah" finds the ute issued to Sarah. Each word is also
 * tried with punctuation squashed out, so registration and serial numbers
 * match however they were typed.
 */
export function matchesEquipmentSearch(eq: Equipment, query: string, typeLabel?: string | null): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = equipmentSearchText(eq, typeLabel);
  const squashed = squash(text);
  // The whole query squashed as well, so "ABC 123" is one rego, not two words.
  if (words.length > 1 && squashed.includes(squash(query))) return true;
  return words.every(w => text.includes(w) || squashed.includes(squash(w)));
}
